// Port of webapp/src/widgets/menu.tsx, without the tippy dependency: the menu
// is absolutely positioned relative to the trigger wrapper.

import {createContext, ReactElement, ReactNode, MouseEvent as ReactMouseEvent, useCallback, useContext, useEffect, useRef, useState} from 'react'
import classnames from 'classnames'
import {Icon, IconName} from './icons'

/**
 * ButtonWithMenu owns the open state and shares it through this context, so a
 * call site writes <ButtonWithMenu button={...}><Menu>…</Menu></ButtonWithMenu>
 * and exactly one positioned .Menu box is rendered.
 */
type MenuState = {open: boolean, close: () => void}
const MenuContext = createContext<MenuState | null>(null)

type MenuProps = {
    children: ReactNode
    onClose?: () => void
    ariaLabel?: string

    /** which edge of the trigger the popup lines up with */
    align?: 'left' | 'right'
}

export function Menu({children, onClose, ariaLabel, align = 'right'}: MenuProps): ReactElement | null {
    const ref = useRef<HTMLDivElement>(null)
    const ctx = useContext(MenuContext)
    const open = ctx ? ctx.open : true

    const close = useCallback(() => {
        if (ctx) {
            ctx.close()
        } else {
            onClose?.()
        }
    }, [ctx, onClose])

    const onDocClick = useCallback((event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
            close()
        }
    }, [close])

    useEffect(() => {
        if (!open) {
            return undefined
        }
        document.addEventListener('click', onDocClick, true)
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('click', onDocClick, true)
            document.removeEventListener('keydown', onKey)
        }
    }, [onDocClick, open, close])

    if (!open) {
        return null
    }

    return (
        <div
            ref={ref}
            className={classnames('Menu absolute top-full mt-1 z-menu', align === 'right' ? 'right-0' : 'left-0')}
            role='menu'
            aria-label={ariaLabel}
            onClick={(event) => event.stopPropagation()}
        >
            {children}
        </div>
    )
}

export function MenuGroup({children, label, className}: {children: ReactNode, label?: string, className?: string}): ReactElement {
    return (
        <div className={classnames('py-1', className)}>
            {label && <div className='px-3 py-1 text-xxs uppercase opacity-60'>{label}</div>}
            {children}
        </div>
    )
}

export function MenuSeparator(): ReactElement {
    return <hr className='my-1 border-0 border-t border-main-fg/10'/>
}

type MenuItemProps = {
    children: ReactNode
    onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void
    icon?: IconName
    iconRight?: IconName
    checked?: boolean
    danger?: boolean
    disabled?: boolean
    href?: string
    className?: string
    description?: string
}

export function MenuItem({children, onClick, icon, iconRight, checked, danger, disabled, href, className, description}: MenuItemProps): ReactElement {
    const content = (
        <>
            <span className='Icon mr-2 w-4 shrink-0'>
                {icon && <Icon name={icon} size={16}/>}
            </span>
            <span className='flex-1 text-left truncate'>
                {children}
                {description && <span className='block text-xxs opacity-60'>{description}</span>}
            </span>
            {checked && <Icon name='check' size={14} className='ml-2 shrink-0'/>}
            {iconRight && <Icon name={iconRight} size={14} className='ml-2 shrink-0'/>}
        </>
    )

    const cls = classnames(
        'Menu__item w-full text-left',
        danger && 'text-[#d8002e]',
        disabled && 'pointer-events-none opacity-40',
        className,
    )

    if (href) {
        return (
            <a
                className={cls}
                href={href}
                target='_blank'
                rel='noreferrer'
                role='menuitem'
            >
                {content}
            </a>
        )
    }
    return (
        <button
            type='button'
            className={cls}
            onClick={onClick}
            disabled={disabled}
            role='menuitem'
        >
            {content}
        </button>
    )
}

type ButtonProps = {
    button: ReactNode
    children: ReactNode
    onOpen?: () => void
    className?: string
    ariaLabel?: string
}

/** Trigger + popup pair used everywhere a dropdown appears. */
export function ButtonWithMenu({button, children, onOpen, className, ariaLabel}: ButtonProps): ReactElement {
    const [open, setOpen] = useState(false)
    const state: MenuState = {open, close: () => setOpen(false)}

    return (
        <div className={classnames('relative inline-flex', className)}>
            <button
                type='button'
                className='inline-flex min-w-0 items-center'
                aria-haspopup='menu'
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={(event) => {
                    event.stopPropagation()
                    setOpen(!open)
                    onOpen?.()
                }}
            >
                {button}
            </button>
            <MenuContext.Provider value={state}>
                {children}
            </MenuContext.Provider>
        </div>
    )
}
